import { formatarMoeda } from './moeda';

/**
 * Converte a obrigação que a API devolve no formulário que a tela edita.
 *
 * 🔴 POR QUE ESTE ARQUIVO EXISTE (10/09/2026)
 *
 * O modal fazia `form = { ...blankForm(), ...obrigacaoDaApi }` — espalhava o
 * payload por cima do formulário e pronto. Os nomes NÃO batem: a API manda
 * objetos aninhados (`category`, `partner`, `creditor_debtor`, `owner`) e o
 * formulário guarda ids soltos (`chart_of_account_id`, `partner_id`,
 * `creditor_debtor_id`, `owner_id`).
 *
 * Efeito, relatado pelo Victor: clicar em Editar zerava categoria, parceiro,
 * credor/devedor e pasta. Vencimento e valor sobreviviam porque são os dois
 * únicos campos cujo nome é igual dos dois lados — o que fazia o defeito
 * parecer aleatório em vez de sistemático.
 *
 * O comentário no `openEdit` da lista dizia que "o mapa de campos que existia
 * aqui foi junto com o modal". Ele saiu de lá e nunca foi escrito aqui: o
 * espalhamento tomou o lugar do mapa sem ninguém notar, porque `{...}` nunca
 * dá erro.
 *
 * Fora do componente e com teste porque um mapa silencioso é a pior espécie de
 * defeito: ele não quebra, só perde dado.
 *
 * @param {object} o      obrigação como a API devolve
 * @param {object} vazio  o formulário em branco, para os padrões
 * @returns {object} formulário pronto para edição
 */
export const obrigacaoParaFormulario = (o, vazio = {}) => {
  if (!o) return { ...vazio };

  return {
    ...vazio,

    // Campos de nome igual dos dois lados.
    id: o.id,
    obligation_type: o.obligation_type || vazio.obligation_type,
    description: o.description || '',
    due_date: o.due_date || '',
    notes: o.notes || '',
    retroactive: Boolean(o.retroactive),

    // 🔴 O valor precisa VOLTAR formatado. A API manda número cru ("1725.3") e
    // o campo é de texto em português: sem formatar, aparece "R$ 1725.3" e —
    // pior — qualquer conta feita em cima dele parte de um texto que a pessoa
    // não reconhece como o valor dela.
    gross_amount: formatarMoeda(o.gross_amount),

    // A parcela é do registro, não do formulário de criação: editar uma
    // obrigação nunca reparcelou nada, e mandar o total de volta faria a tela
    // oferecer "gerar parcelas" de novo.
    total_installments: String(o.total_installments || 1),

    // Objetos aninhados -> ids. Era exatamente isto que se perdia.
    chart_of_account_id: o.category?.id ?? null,
    partner_id: o.partner?.id ?? null,
    creditor_debtor_id: o.creditor_debtor?.id ?? null,
    creditor_debtor_name: o.creditor_debtor?.name || '',
    bank_account_id: o.bank_account?.id ?? vazio.bank_account_id ?? null,
    owner_type: o.owner?.type ?? null,
    owner_id: o.owner?.id ?? null,
    owner_label: o.owner?.folder_number || o.owner?.label || '',

    // Encargos: o que o registro tem manda; o padrão do escritório só entra
    // quando o registro não tem nada, senão editar uma cobrança antiga
    // aplicaria juros que ela nunca teve.
    interest_percent: o.interest_percent ?? vazio.interest_percent,
    fine_percent: o.fine_percent ?? vazio.fine_percent,
    billing_type: o.billing_type || vazio.billing_type,
  };
};

/**
 * Nome do parceiro para o campo de busca, que mostra texto e guarda id.
 */
export const nomeDoParceiro = o => o?.partner?.name || '';
